import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, IconButton, Typography } from '@shared/ui';
import {
  InfoIcon,
  CloseIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleChevronRightIcon,
} from '@shared/ui/icons';
import {
  INFO_PANE_PAGES,
  closeInfoPane,
  openPreviousPageFromHistory,
  openNextPageFromHistory,
  toggleIsInfoPaneExpanded,
} from 'redux_store';
import {
  selectCurrentPage,
  selectCurrentPageName,
  selectIsExpanded,
  selectCanGoBack,
  selectCanGoForward,
} from '../model/selectors';
import { INFO_PANE_WIDTHS } from '../lib/constants';
import PatientPane from './PatientPane';
import MetricsPane from './MetricsPane';
import { Pane, Header, Body, Footer, CollapsedRail } from './InfoPane.styles';

const PAGE_TO_COMPONENT = {
  [INFO_PANE_PAGES.patient_page]: <PatientPane />,
  [INFO_PANE_PAGES.metrics]: <MetricsPane />,
};

const PAGE_TO_BREADCRUMB = {
  [INFO_PANE_PAGES.patient_page]: 'patient',
  [INFO_PANE_PAGES.metrics]: 'metrics',
};

/**
 * InfoPane shell — the layout-column surface inside AppShell. Routes
 * between Patient / Metrics sub-pages via currentPageName, owns the
 * header (breadcrumb + history arrows + close), the collapse footer
 * (single CTA on the Patient page — Collapse), and the 48 px
 * collapsed rail click target.
 *
 * Width is fixed at INFO_PANE_WIDTHS.defaultExpanded (380 px) when
 * expanded. Collapsed = 48 px rail, governed by isInfoPaneExpanded
 * slice flag. Drag-resize was rejected by product — see decision log.
 *
 * Renders nothing when no page is open (currentPage.uuid is null).
 */
// MUI's IconButton takes its colour from the MUI palette, which only
// describes the light theme — leaving these controls near-black on the
// dark pane. Naming the tokens keeps them legible in both, and matches
// how every other element in this widget is coloured.
const HEADER_ICON_SX = {
  color: 'var(--qhs-text-2)',
  '&:hover': {
    color: 'var(--qhs-text-1)',
    backgroundColor: 'var(--qhs-surface-hover)',
  },
  '&.Mui-disabled': {
    color: 'var(--qhs-text-4)',
  },
};

const InfoPane = () => {
  const dispatch = useDispatch();
  const i18n = useSelector(state => state.i18n);
  const { infoPane } = i18n;
  const currentPage = useSelector(selectCurrentPage);
  const currentPageName = useSelector(selectCurrentPageName);
  const isExpanded = useSelector(selectIsExpanded);
  const canGoBack = useSelector(selectCanGoBack);
  const canGoForward = useSelector(selectCanGoForward);

  const collapsed = !isExpanded;

  const expand = useCallback(() => dispatch(toggleIsInfoPaneExpanded(true)), [dispatch]);
  const collapse = useCallback(() => dispatch(toggleIsInfoPaneExpanded(false)), [dispatch]);
  const close = useCallback(() => dispatch(closeInfoPane()), [dispatch]);

  // Hidden state: nothing to show.
  if (!currentPage?.uuid) return null;

  // Collapsed rail — whole pane is the click target to expand.
  if (collapsed) {
    return (
      <Pane $collapsed onClick={expand} aria-label="Expand info pane">
        <CollapsedRail>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(46, 72, 211, 0.08)',
              color: 'var(--qhs-brand)',
            }}
          >
            <InfoIcon style={{ width: 16, height: 16 }} />
          </Box>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--qhs-surface)',
              border: '1px solid var(--qhs-border)',
              color: 'var(--qhs-text-2)',
            }}
          >
            <DoubleChevronRightIcon style={{ width: 14, height: 14, transform: 'scaleX(-1)' }} />
          </Box>
        </CollapsedRail>
      </Pane>
    );
  }

  // Expanded layout.
  const breadcrumbKey = PAGE_TO_BREADCRUMB[currentPageName];
  const breadcrumbLabel = breadcrumbKey ? infoPane[breadcrumbKey] : '';
  const showFooter = currentPageName === INFO_PANE_PAGES.patient_page;

  return (
    <Pane $width={INFO_PANE_WIDTHS.defaultExpanded}>
      <Header>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon style={{ width: 14, height: 14, color: 'var(--qhs-brand)' }} />
          <Typography
            sx={{
              fontFamily: 'var(--qhs-font-head)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1.4,
              textTransform: 'uppercase',
              color: 'var(--qhs-text-3)',
            }}
          >
            {breadcrumbLabel}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <IconButton
            size="small"
            sx={HEADER_ICON_SX}
            disabled={!canGoBack}
            onClick={() => dispatch(openPreviousPageFromHistory())}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            size="small"
            sx={HEADER_ICON_SX}
            disabled={!canGoForward}
            onClick={() => dispatch(openNextPageFromHistory())}
          >
            <ChevronRightIcon />
          </IconButton>
          <Box sx={{ width: 1, height: 18, background: 'var(--qhs-border)', mx: 0.5 }} />
          <IconButton size="small" sx={HEADER_ICON_SX} onClick={close}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Header>
      <Body>{PAGE_TO_COMPONENT[currentPageName] ?? null}</Body>
      {showFooter && (
        <Footer>
          <Button variant="secondary" onClick={collapse} startIcon={<DoubleChevronRightIcon />}>
            {infoPane.collapseButton}
          </Button>
        </Footer>
      )}
    </Pane>
  );
};

export default InfoPane;
